import { z } from "zod";

// Request DTO
export const atsCheckSchema = z.object({
  resumeContent: z.string().min(1, "Resume content is required"),
  jobDescription: z.string().min(1, "Job description is required"),
  mainRequirements: z.string().optional(),
});

export type AtsCheckDto = z.infer<typeof atsCheckSchema>;

// AI Content Detection Section Result
export const aiSectionSchema = z.object({
  name: z.string(), // Section name like "Summary", "Experience-CompanyX", etc.
  score: z.number().min(0).max(100),
  issues: z.object({
    repeatedStructure: z.boolean(),
    genericPhrases: z.boolean(),
    unnaturalWording: z.boolean(),
    lackSpecificity: z.boolean(),
    excessiveBuzzwords: z.boolean(),
  }),
  evidence: z.array(z.string()), // Exact phrases from resume
});

export type AiSectionDto = z.infer<typeof aiSectionSchema>;

// AI Content Detection Result
export const aiContentDetectionSchema = z.object({
  overall: z.object({
    score: z.number().min(0).max(100),
    confidence: z.enum(["low", "medium", "high"]),
    label: z.enum(["fully_human", "likely_ai", "fully_ai"]),
  }),
  sections: z.array(aiSectionSchema),
  summary: z.string(), // Overall explanation
});

export type AiContentDetectionDto = z.infer<typeof aiContentDetectionSchema>;

// ATS Score Result
export const atsScoreSchema = z.object({
  score: z.number().min(0).max(100), // Overall ATS score percentage
  matchedScore: z.number().min(0).max(100).optional(),
  missingScore: z.number().min(0).max(100).optional(),
  matchedKeywords: z.array(z.string()),
  missingKeywords: z.array(z.string()),
  suggestions: z.array(z.string()),
});

export type AtsScoreDto = z.infer<typeof atsScoreSchema>;

export const repetitionRubricItemSchema = z.object({
  score: z.number().min(0).max(100),
  details: z.string().optional(),
});

export const repetitionSectionSchema = z.object({
  name: z.string(),
  score: z.number().min(0).max(100).optional(),
  reasons: z.array(z.string()).optional(),
  highlights: z.array(z.string()).optional(),
  responsibilities: repetitionRubricItemSchema.optional(),
  duties: repetitionRubricItemSchema.optional(),
  skills: repetitionRubricItemSchema.optional(),
  operations: repetitionRubricItemSchema.optional(),
  repetition: z.object({
    score: z.number().min(0).max(100),
    details: z.string(),
    highlights: z.array(z.string()),
  }).optional(),
});

export type RepetitionSectionDto = z.infer<typeof repetitionSectionSchema>;

// Repetition Checker Result
export const repetitionCheckerSchema = z.object({
  score: z.number().min(0).max(100), // Growth/uniqueness score
  growthPattern: z.enum(["excellent", "good", "average", "poor", "concerning"]),
  analysis: z.string(), // Detailed analysis explanation
  identicalPositions: z.array(z.string()), // List of positions that are too similar
  recommendations: z.array(z.string()), // Suggestions for improvement
  sections: z.array(repetitionSectionSchema).optional(),
  positionProgression: z.array(z.object({
    position: z.string(),
    level: z.enum(["entry", "junior", "mid", "senior", "lead", "executive"]),
    uniqueness: z.number().min(0).max(100),
    issues: z.array(z.string()),
  })),
});

export type RepetitionCheckerDto = z.infer<typeof repetitionCheckerSchema>;

// Combined Response DTO
export const atsCheckResponseSchema = z.object({
  aiContentDetection: aiContentDetectionSchema,
  atsScore: atsScoreSchema,
  repetitionChecker: repetitionCheckerSchema,
  overallScore: z.number().min(0).max(100), // Combined score
});

export type AtsCheckResponseDto = z.infer<typeof atsCheckResponseSchema>;

